import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { DATA_KEY_MULTI_FREE_GIFT_PROMO } from '@/data/constants/hdmDataKey';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { getSettings, useSettings } from '@/data/Settings';
import { Cache } from '@/data/types/Cache';
import { getUser } from '@/data/User-Server';
import { getPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { customPromotionDetails } from 'integration/generated/custom';
import { RetrieveMultiFreeGiftDetails } from 'integration/generated/custom/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize as unstableSerialize } from 'swr';

// declare fetcher
export const multiFreeGiftPromotionDetailsFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			catentryIds: string;
			currentPhysicalStoreId: string;
			langId: string;
			responseFormat?: 'json';
		},
		params: RequestParams
	) => {
		const response = (await customPromotionDetails(pub).retrieveMultiFreeGiftDetails(
			storeId,
			{
				...query,
				responseFormat: 'json' as const,
			},
			params
		)) as RetrieveMultiFreeGiftDetails;
		return await response;
	};

export const buildMultiFreeGiftPromoKey = ({
	catentryIds,
	storeId,
	currentPhysicalStoreId,
	langId,
}: {
	storeId: string;
	catentryIds: string;
	currentPhysicalStoreId: string;
	langId: string;
}) => [
	shrink({
		storeId,
		catentryIds,
		currentPhysicalStoreId,
		langId,
		responseFormat: 'json',
	}),
	DATA_KEY_MULTI_FREE_GIFT_PROMO,
];

//  Declear server side function to get Multi Free Gift Promo Details
export const getMultiFreeGiftPromoDetails = async (
	cache: Cache,
	context: GetServerSidePropsContext,
	catentryIds: string
): Promise<RetrieveMultiFreeGiftDetails> => {
	const settings = await getSettings(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);
	const user = await getUser(cache, context);
	const physicalStoreId = getPhysicalStoreId(context);
	const query = {
		catentryIds,
		currentPhysicalStoreId: physicalStoreId,
		langId,
		responseFormat: 'json',
	} as const;
	const props = { storeId, ...query };
	const key = unstableSerialize([shrink(props), DATA_KEY_MULTI_FREE_GIFT_PROMO]);
	const params = {};

	const cacheScope = getServerCacheScope(context, user.context);

	const value =
		(cache.get(key, cacheScope) as Promise<RetrieveMultiFreeGiftDetails | undefined>) ||
		multiFreeGiftPromotionDetailsFetcher(false)(storeId, query, params);
	const response = await value;
	response && cache.set(key, response, cacheScope);
	return response ?? ({} as RetrieveMultiFreeGiftDetails);
};

/**
 * Extract the free-gift promo code (PGC / PGNC / TNC-PGC / TNC-PGNC / PG) for a catentry from the
 * multi-free-gift endpoint response (`promotionData` is keyed by catentryId).
 */
export const getMultiFreeGiftPromoCode = (
	details: RetrieveMultiFreeGiftDetails | undefined,
	catentryId: string
): string | undefined => details?.promotionData?.[catentryId]?.dlPromoCode || undefined;

/**
 * Build a `catentryId -> dlPromoCode` map from a batched multi-free-gift response
 * (`?catentryIds=cat1,cat2,cat3`). Used by lists that resolve every PG product in one request.
 */
export const getMultiFreeGiftPromoMap = (
	details: RetrieveMultiFreeGiftDetails | undefined
): Map<string, string> => {
	const map = new Map<string, string>();

	Object.entries(details?.promotionData ?? {}).forEach(([catentryId, value]) => {
		if (value?.dlPromoCode) map.set(catentryId, value.dlPromoCode);
	});

	return map;
};

//  Declear client side hook to get Multi Free Gift Promo Details
export const useMultiFreeGiftPromoDetails = (catentryIds: string) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { physicalStoreId } = useDefaultStore();
	const query = {
		catentryIds,
		currentPhysicalStoreId: physicalStoreId,
		langId,
		responseFormat: 'json',
	} as const;
	const props = { storeId, ...query };
	const { data, error, isLoading, isValidating } = useSWR(
		storeId && catentryIds ? buildMultiFreeGiftPromoKey(props) : null,
		async ([]) => await multiFreeGiftPromotionDetailsFetcher(true)(storeId, query, params),
		{ use: [laggyMiddleWare] }
	);
	return {
		data,
		loading: !error && isLoading,
		isValidating,
		error,
	};
};
